import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Shield } from "lucide-react";

const CONTACT_EMAIL = "suporte@ziros.com.br";
const SUPPORT_EMAIL = "suporte@ziros.com.br";
const CONTROLLER_NAME = "MATHEUS PHELIPE DE OLIVEIRA — Ziros";
const CONTROLLER_CNPJ = "55.508.912/0001-60";
const UPDATED_AT = "16 de outubro de 2026";

const SECTIONS = {
  privacidade: {
    title: "Política de Privacidade",
    subtitle: "Nafe Entregador e plataforma Nafe Logística — Lei 13.709/2018 (LGPD)",
    body: (
      <>
        <p>
          Esta Política descreve como a plataforma <strong>Nafe Logística</strong>, operada por{" "}
          <strong>{CONTROLLER_NAME}</strong> (CNPJ {CONTROLLER_CNPJ}), trata dados pessoais no
          aplicativo <strong>Nafe Entregador</strong> e na plataforma web associada.
        </p>

        <h2>1. Controladora</h2>
        <p>
          Controladora dos dados: <strong>{CONTROLLER_NAME}</strong><br />
          CNPJ: <strong>{CONTROLLER_CNPJ}</strong><br />
          Nome fantasia / produto: Nafe Logística<br />
          Canal de privacidade e suporte:{" "}
          <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
        </p>

        <h2>2. Quem é o titular</h2>
        <p>
          Esta política cobre entregadores, operadores, administradores e demais usuários autenticados
          do app e do sistema. Destinatários de intimações/documentos são tratados conforme a operação
          contratada pelo cliente (cartório/cliente), na medida necessária à entrega.
        </p>

        <h2>3. Dados que coletamos</h2>
        <ul>
          <li><strong>Conta:</strong> nome, e-mail, perfil de acesso e dados de autenticação.</li>
          <li><strong>Operação:</strong> documentos atribuídos, endereços de entrega, status, ocorrências e checklist.</li>
          <li><strong>Localização:</strong> GPS em uso, para montar a rota, registrar a entrega e permitir rastreamento operacional autorizado.</li>
          <li><strong>Mídia:</strong> fotos/evidências da câmera ou galeria, apenas quando o usuário registra a entrega.</li>
          <li><strong>Dispositivo/técnicos:</strong> identificação de sessão, logs de segurança e metadados necessários à operação do app.</li>
        </ul>

        <h2>4. Finalidades e bases legais</h2>
        <ul>
          <li>Execução do contrato / prestação do serviço de logística e entrega (art. 7º, V, LGPD).</li>
          <li>Legítimo interesse operacional e segurança (art. 7º, IX), com equilíbrio e minimização.</li>
          <li>Cumprimento de obrigação legal ou regulatória, quando aplicável (art. 7º, II).</li>
          <li>Consentimento para permissões de dispositivo (localização e câmera), concedido no sistema operacional e revogável nas configurações do aparelho.</li>
        </ul>

        <h2>5. Compartilhamento</h2>
        <p>
          Dados podem ser acessados por administradores/operadores do tenant (cartório/cliente) e por
          prestadores técnicos necessários à operação (hospedagem, armazenamento de evidências, mapas),
          sempre sob contrato e finalidade limitada. Não vendemos dados pessoais.
        </p>

        <h2>6. Armazenamento e retenção</h2>
        <p>
          Dados ficam em infraestrutura contratada pela Nafe (incluindo nuvem). Evidências e registros
          operacionais podem ser mantidos pelo prazo exigido pelo cliente ou pela lei. Contas podem
          exercer portabilidade e anonimização pelos canais do sistema (área Segurança / LGPD) ou pelo e-mail de privacidade.
        </p>

        <h2>7. Segurança</h2>
        <p>
          Adotamos controles técnicos e organizacionais: autenticação, tokens de acesso com renovação,
          isolamento por perfil/tenant, trilha de auditoria e criptografia de dados sensíveis em repouso,
          conforme desenho da plataforma.
        </p>

        <h2>8. Direitos do titular (LGPD)</h2>
        <p>Você pode solicitar, na medida aplicável:</p>
        <ul>
          <li>confirmação e acesso aos dados;</li>
          <li>correção de dados incompletos ou desatualizados;</li>
          <li>anonimização, bloqueio ou eliminação de dados desnecessários;</li>
          <li>portabilidade;</li>
          <li>informação sobre compartilhamentos;</li>
          <li>revogação de consentimento de permissões do aparelho.</li>
        </ul>
        <p>
          No app/web autenticado: use <strong>Segurança → Privacidade (LGPD)</strong> para exportar dados
          ou solicitar anonimização. Também: <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
        </p>

        <h2>9. Crianças e adolescentes</h2>
        <p>O app é destinado a uso profissional por entregadores adultos autorizados pelo operador.</p>

        <h2>10. Alterações</h2>
        <p>
          Podemos atualizar esta política. A data de vigência aparece no topo da página. Uso continuado
          após mudanças relevantes implica ciência da nova versão, sem prejuízo dos direitos legais.
        </p>

        <h2>11. Contato</h2>
        <p>
          Privacidade: <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a><br />
          Suporte: <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
        </p>
      </>
    ),
  },
  termos: {
    title: "Termos de Uso",
    subtitle: "Aplicativo Nafe Entregador",
    body: (
      <>
        <p>
          Ao acessar o <strong>Nafe Entregador</strong>, você concorda com estes Termos. Se não concordar,
          não utilize o aplicativo.
        </p>

        <h2>1. Objeto</h2>
        <p>
          O app permite que entregadores autenticados visualizem rotas, naveguem até destinos, registrem
          entregas/ocorrências e enviem evidências à plataforma Nafe Logística.
        </p>

        <h2>2. Conta e acesso</h2>
        <ul>
          <li>O acesso é pessoal e intransferível, concedido pelo administrador/operador.</li>
          <li>Você é responsável por manter a confidencialidade da senha e do aparelho.</li>
          <li>Uso indevido pode resultar em bloqueio da conta.</li>
        </ul>

        <h2>3. Uso permitido</h2>
        <ul>
          <li>Utilizar o app apenas para a operação de entrega autorizada.</li>
          <li>Registrar informações e evidências verdadeiras.</li>
          <li>Respeitar leis de trânsito e normas do contratante.</li>
        </ul>

        <h2>4. Localização e câmera</h2>
        <p>
          O app solicita permissão de localização e câmera para cumprir a finalidade logística.
          Você pode revogar permissões nas configurações do sistema; algumas funções deixarão de funcionar.
        </p>

        <h2>5. Propriedade intelectual</h2>
        <p>
          Marcas, layout, código e conteúdos da Nafe/ZIROS pertencem aos respectivos titulares.
          É vedada cópia ou engenharia reversa não autorizada.
        </p>

        <h2>6. Disponibilidade</h2>
        <p>
          Nos esforçamos para manter o serviço disponível, mas não garantimos ausência de interrupções
          (rede, lojas, GPS, terceiros de mapa). Em falha, registre a ocorrência conforme procedimento do operador.
        </p>

        <h2>7. Limitação</h2>
        <p>
          Na medida permitida pela lei, a Nafe não se responsabiliza por danos decorrentes de uso
          inadequado do app, falha de GPS do aparelho ou decisões operacionais do entregador no campo.
        </p>

        <h2>8. Privacidade</h2>
        <p>
          O tratamento de dados pessoais está descrito na{" "}
          <Link to="/privacidade">Política de Privacidade</Link>.
        </p>

        <h2>9. Contato</h2>
        <p>
          Suporte: <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a><br />
          Privacidade: <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
        </p>
      </>
    ),
  },
  suporte: {
    title: "Suporte — Nafe Entregador",
    subtitle: "Canais oficiais para lojas e usuários",
    body: (
      <>
        <p>
          Precisa de ajuda com login, rota, GPS ou evidências? Fale conosco:
        </p>
        <ul>
          <li>E-mail operacional: <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a></li>
          <li>Privacidade / LGPD: <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a></li>
        </ul>
        <p>
          Informe o e-mail da conta, cidade e uma descrição objetiva do problema.
          Se possível, anexe print da tela (sem dados sensíveis de terceiros).
        </p>
        <p>
          Documentos legais:{" "}
          <Link to="/privacidade">Política de Privacidade</Link>
          {" · "}
          <Link to="/termos">Termos de Uso</Link>
        </p>
      </>
    ),
  },
};

export default function PublicLegal() {
  const { pathname } = useLocation();
  const key = pathname.includes("termos")
    ? "termos"
    : pathname.includes("suporte")
      ? "suporte"
      : "privacidade";
  const section = SECTIONS[key];

  return (
    <div className="min-h-screen bg-[var(--bg-main)] text-[var(--text-1)]">
      <header className="border-b border-[var(--border-c)] bg-[var(--surface-1)]">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-[var(--brand)] text-white flex items-center justify-center">
              <Shield size={18} />
            </div>
            <div>
              <div className="font-display font-bold text-sm">Nafe Logística</div>
              <div className="text-[11px] text-[var(--text-3)]">Documentos legais</div>
            </div>
          </div>
          <nav className="flex flex-wrap gap-3 text-xs font-semibold">
            <Link className={key === "privacidade" ? "text-[var(--brand)]" : "text-[var(--text-2)]"} to="/privacidade">Privacidade</Link>
            <Link className={key === "termos" ? "text-[var(--brand)]" : "text-[var(--text-2)]"} to="/termos">Termos</Link>
            <Link className={key === "suporte" ? "text-[var(--brand)]" : "text-[var(--text-2)]"} to="/suporte">Suporte</Link>
          </nav>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8 sm:py-10">
        <p className="text-xs text-[var(--text-3)] mb-2">Atualizado em {UPDATED_AT}</p>
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">{section.title}</h1>
        <p className="text-sm text-[var(--text-2)] mt-2 mb-8">{section.subtitle}</p>
        <article className="legal-prose card-soft p-5 sm:p-8 text-sm leading-relaxed text-[var(--text-2)] space-y-4">
          {section.body}
        </article>
        <p className="text-xs text-[var(--text-3)] mt-8">
          <Link to="/login" className="text-[var(--brand)] font-semibold">Voltar ao login</Link>
        </p>
      </main>

      <style>{`
        .legal-prose h2 { color: var(--text-1); font-weight: 700; font-size: 1rem; margin-top: 1.25rem; }
        .legal-prose ul { list-style: disc; padding-left: 1.25rem; space: 0.35rem; }
        .legal-prose li { margin: 0.35rem 0; }
        .legal-prose a { color: var(--brand); font-weight: 600; text-decoration: underline; text-underline-offset: 2px; }
        .legal-prose strong { color: var(--text-1); }
      `}</style>
    </div>
  );
}
